import * as React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import { ListItem, ListItemIcon, useMediaQuery } from '@mui/material';
import { Loader, Typography } from '@sm-dashboard/ui-components';
import { useState, useRef, useEffect, useCallback } from 'react';
import chabadLogo from '@/assets/chabad-logo.png';
import chabadLogoWorld from '@/assets/chabad-logo-world.png';
import { donationsRoute, isWorldShalichUser, myPageRoute, pagesList, CONTACT_US_NUMBER, worldFriendsShluchimTag } from '@/constants';
import { useNavigate } from 'react-router-dom';
import ProfileSection from './ProfileSection';
import HoverMenu from './HoverMenu';
import { useTheme } from '@mui/material/styles';
import MobileDashboardChrome from './MobileDashboardLayout';
import { SAFE_AREA_TOP } from './mobileLayoutConstants';
import { useAppContext } from '@/context/context';
import { useScrollPosition } from '@/hooks/useSctollPosition';
import { useAppViewportHeight } from '@/hooks/useAppViewportHeight';
import { getUser } from '@/util/getUser';
import PageTitleHeader from '@/components/common/PageTitleHeader';
import PaymentRequiredDialog from '@/components/dashboard/PaymentRequiredDialog';
import NewDesignAnnouncementDialog from '@/components/dashboard/NewDesignAnnouncementDialog';
import RequiredPersonalProfileDialog, {
  useRequiredPersonalProfileGate
} from '@/components/dashboard/RequiredPersonalProfileDialog';
import { getAllModulesByRoute } from '@/constants/paymentModules';
import { worldDonationsPaymentGate } from '@/constants/paymentModulesWorld';
import { Page, PaymentModule } from '@sm-dashboard/types';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';
import { CrmAiChat, CrmAiChatProvider } from '@/components/dashboard/crm/aiChat';

const drawerWidth = 95;
const sidebarGap = 20;

const isPageMenuChildVisible = (child: Page, hasPagePermission: (page: string) => boolean) =>
  Boolean(child.menuAction || hasPagePermission(child.link || ''));

const isNavItemVisible = (item: Page, hasPagePermission: (page: string) => boolean) => {
  const hasChildren = item.children && item.children.length > 0;
  if (!hasChildren) {
    return hasPagePermission(item.link || '');
  }
  return item.children!.some(child => isPageMenuChildVisible(child, hasPagePermission));
};

const normalizeNavPath = (path: string) => {
  const normalized = path.endsWith('/') ? path.slice(0, -1).toLowerCase() : path.toLowerCase();
  return normalized || '/';
};

const isRouteMatch = (routeLink: string | undefined, selectedLink: string) => {
  if (!routeLink) return false;
  const normalizedRoute = normalizeNavPath(routeLink);
  return (
    selectedLink === normalizedRoute || (normalizedRoute !== '/' && selectedLink.startsWith(`${normalizedRoute}/`))
  );
};

const isNavItemSelected = (item: Page, selectedLink: string): boolean => {
  if (isRouteMatch(item.link, selectedLink)) return true;
  return item.children?.some(child => isNavItemSelected(child, selectedLink)) ?? false;
};

interface DashboardLayoutProps {
  children: React.ReactNode;
  bgColor?: string;
}

const DashboardLayout = ({ children, bgColor }: DashboardLayoutProps) => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [paymentDialog, setPaymentDialog] = useState<{
    open: boolean;
    modules: PaymentModule[] | null;
  }>({ open: false, modules: null });
  const [showNewDesignDialog, setShowNewDesignDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);
  const navigate = useNavigate();
  const closeMenuTimer = useRef<(NodeJS.Timeout | null)[]>(Array(pagesList.length).fill(null));
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  useAppViewportHeight();

  const { selectedNav, userPermission, setUser } = useAppContext();
  const {
    isIncomplete: mustCompletePersonalProfile,
    showDialog: showRequiredProfileDialog,
    missingFields,
    goToProfile
  } = useRequiredPersonalProfileGate();
  const user = getUser();
  const navPages = pagesList;

  const { scrollRef, restoreScroll, saveScroll, scrollToBottom } = useScrollPosition({
    keyForPersist: 'nav-scroll-pos',
    storage: 'context'
  });

  const updateBottomIndicator = useCallback(() => {
    const listEl = scrollRef.current;
    if (!listEl) return;
    const { scrollTop, scrollHeight, clientHeight } = listEl;
    setShowBottomIndicator(scrollTop + clientHeight < scrollHeight - 4);
  }, [scrollRef]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    if (closeMenuTimer.current[index]) {
      clearTimeout(closeMenuTimer.current[index]!);
      closeMenuTimer.current[index] = null;
    }
    closeMenuTimer.current.forEach((timerId, i) => {
      if (i !== index && timerId) {
        clearTimeout(timerId);
        closeMenuTimer.current[i] = null;
      }
    });
    setOpenMenuIndex(index);
  };

  const handleMenuClose = (index: number, option?: Page) => {
    if (closeMenuTimer.current[index]) {
      clearTimeout(closeMenuTimer.current[index]!);
      closeMenuTimer.current[index] = null;
    }
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    }
    if (option?.menuAction === 'logout') {
      setShowLogoutDialog(true);
      return;
    }
    if (option?.menuAction === 'contact') {
      window.open(`https://wa.me/${CONTACT_US_NUMBER}`, '_blank');
      return;
    }
    if (option?.link) {
      navigate(option.link);
      saveScroll();
    }
  };

  const handleLogout = () => {
    window.localStorage.removeItem('token');
    setUser(undefined);
    navigate('/login');
  };

  const handleMouseLeave = (index: number) => {
    if (openMenuIndex === index) {
      if (closeMenuTimer.current[index]) {
        clearTimeout(closeMenuTimer.current[index]!);
      }
      closeMenuTimer.current[index] = setTimeout(() => {
        handleMenuClose(index);
      }, 250);
    }
  };

  const handleMouseEnterMenu = (index: number) => {
    if (closeMenuTimer.current[index]) {
      clearTimeout(closeMenuTimer.current[index]!);
      closeMenuTimer.current[index] = null;
    }
  };

  // Authentication and permission logic from original
  useEffect(() => {
    if (!user) {
      const appToken = getParameterByName('web2applicationtoken');
      if (appToken) localStorage.setItem('appUserToken', appToken);
    }
  }, [user]);

  // Check if should show welcome dialog (only for world shluchim users, once per browser)
  useEffect(() => {
    if (isWorldShalichUser(userPermission)) {
      const hasSeenWelcome = localStorage.getItem('hasSeenWorldShalichWelcomeDialog');
      if (!hasSeenWelcome) {
        const timer = setTimeout(() => {
          setShowNewDesignDialog(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [userPermission]);

  // Check for payment required modules on route change
  useEffect(() => {
    if (mustCompletePersonalProfile) {
      setPaymentDialog({ open: false, modules: null });
      return;
    }
    if (userPermission && selectedNav?.link) {
      const modules = getAllModulesByRoute(selectedNav.link, isWorldShalichUser(userPermission));
      if (modules?.length) {
        const isDonationsRoute =
          selectedNav.link === donationsRoute || selectedNav.link.startsWith(`${donationsRoute}/`);
        const hasRoutePermission =
          modules.some(module => userPermission.hasTag(module.tag)) ||
          (isDonationsRoute && userPermission.hasTag(worldFriendsShluchimTag));
        if (!hasRoutePermission && !userPermission.isAdmin) {
          const isWorldDonationsGate = isWorldShalichUser(userPermission) && isDonationsRoute;
          setPaymentDialog({
            open: true,
            modules: isWorldDonationsGate ? [worldDonationsPaymentGate] : modules
          });
        } else {
          setPaymentDialog({ open: false, modules: null });
        }
      } else {
        setPaymentDialog({ open: false, modules: null });
      }
    }
  }, [selectedNav?.link, userPermission, mustCompletePersonalProfile]);

  useEffect(() => {
    restoreScroll();
  }, [selectedNav, restoreScroll]);

  useEffect(() => {
    const listEl = scrollRef.current;
    if (!listEl) return;
    const handleScroll = () => updateBottomIndicator();
    updateBottomIndicator();
    listEl.addEventListener('scroll', handleScroll);
    return () => listEl.removeEventListener('scroll', handleScroll);
    // isSmallScreen: the sidebar (and its scroll list) remounts when crossing the md breakpoint
  }, [scrollRef, updateBottomIndicator, userPermission, isSmallScreen]);

  const getParameterByName = (name: string, url = window.location.href) => {
    name = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  };

  const handleNewDesignDialogClose = () => {
    localStorage.setItem('hasSeenWorldShalichWelcomeDialog', 'true');
    setShowNewDesignDialog(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('hasSeenWorldShalichWelcomeDialog', 'true');
    setShowNewDesignDialog(false);
  };

  const handleInitialization = () => {
    if (!userPermission) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Loader />
        </Box>
      );
    }

    // אם יש דיאלוג תשלום פתוח, הצג אותו במקום התוכן
    if (paymentDialog.open && paymentDialog.modules) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            p: 3
          }}
        >
          <PaymentRequiredDialog modules={paymentDialog.modules} />
        </Box>
      );
    }

    // בדיקת הרשאות לפי תגיות
    const isBlocked = () => {
      if (userPermission && userPermission.isAdmin) {
        return false;
      }

      if (!userPermission || !selectedNav.link) {
        return true;
      }

      // בדיקה אם המשתמש יש לו הרשאות לנתיב הספציפי
      const hasRoutePermission = userPermission.hasPagePermission(selectedNav.link);
      const blocked = !hasRoutePermission;
      return blocked;
    };

    if (isBlocked()) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            p: 3
          }}
        >
          <Box component="h1" sx={{ typography: 'h5', color: 'primary.main', m: 0, mb: '0.35em' }}>
            אין הרשאות גישה
          </Box>
          <Typography variant="body" color="primary.main">
            אין לך הרשאות לצפות בדף זה. אנא פנה למנהל המערכת.
          </Typography>
        </Box>
      );
    }

    return children;
  };

  // Desktop and mobile share one element tree for the page content: only the chrome
  // (sidebar / bottom nav) is swapped when crossing the md breakpoint, so the mounted
  // page keeps its state (e.g. an open form) when the window is resized.
  return (
    <CrmAiChatProvider>
      <Box
        sx={{
          // Use measured --app-height (window.innerHeight) — 100vh/100dvh/-webkit-fill-available
          // leave letterbox gaps or break nested scroll in iOS App Store WKWebViews.
          height: isSmallScreen ? 'var(--app-height, 100vh)' : '100vh',
          position: isSmallScreen ? 'fixed' : 'relative',
          ...(isSmallScreen
            ? {
                top: 0,
                left: 0,
                right: 0,
                width: '100%'
              }
            : {}),
          backgroundColor: isSmallScreen ? 'background.default' : '#E6E9F3',
          padding: isSmallScreen ? 0 : 2,
          boxSizing: 'border-box',
          overflow: isSmallScreen ? 'hidden' : 'visible'
        }}
      >
        {/* Inner shell: safe-area inset for notch without leaving a white band outside the app paint. */}
        <Box
          sx={{
            height: '100%',
            width: '100%',
            boxSizing: 'border-box',
            pt: isSmallScreen ? SAFE_AREA_TOP : 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: isSmallScreen ? 'hidden' : 'visible'
          }}
        >
          {/* Hover menu */}
          {!isSmallScreen &&
            openMenuIndex !== null &&
            navPages[openMenuIndex] &&
            userPermission &&
            navPages[openMenuIndex]?.children?.some(child =>
              isPageMenuChildVisible(child, page => userPermission.hasPagePermission(page))
            ) && (
              <HoverMenu
                openMenuIndex={openMenuIndex}
                menuChildren={navPages[openMenuIndex]?.children || []}
                handleMouseLeave={handleMouseLeave}
                handleMenuClose={handleMenuClose}
                handleMouseEnterMenu={handleMouseEnterMenu}
                drawerWidth={drawerWidth}
              />
            )}
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
              boxSizing: 'border-box',
              gap: isSmallScreen ? 0 : `${sidebarGap}px`
            }}
          >
            {!isSmallScreen && (
              <Box
                sx={{
                  width: drawerWidth,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 2,
                  gap: 1.25,
                  height: '100%',
                  borderRadius: '20px',
                  background: '#FFFFFF6B',
                  boxShadow: '0 4px 50px rgba(98, 112, 131, 0.10), inset 0 0 10px rgba(255, 255, 255, 0.55)',
                  overflow: 'hidden',
                  overflowX: 'hidden',
                  boxSizing: 'border-box'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    width: '100%',
                    gap: 1.25,
                    flex: 1,
                    overflow: 'hidden',
                    overflowX: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      flex: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden',
                      overflowX: 'hidden'
                    }}
                  >
                    <List
                      ref={ref => (scrollRef.current = ref as HTMLElement)} // הוספנו את ה-scrollRef כאן
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5,
                        width: '100%',
                        py: 1.5,
                        // כאן הגלילה - רק לרשימת הדפים
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        flex: 1,
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                          display: 'none',
                          width: 0,
                          height: 0
                        },
                        direction: 'rtl'
                      }}
                    >
                      {navPages.map((item, index) => {
                        const hasChildren = item.children && item.children.length > 0;
                        const isSelected =
                          isNavItemSelected(item, normalizeNavPath(selectedNav?.link || '')) || openMenuIndex === index;

                        if (!userPermission) {
                          return null;
                        }

                        const hasPagePermission = (page: string) => userPermission.hasPagePermission(page);
                        const hasOtherVisibleNavItems = navPages.some(
                          navItem => !navItem.isAccountMenu && isNavItemVisible(navItem, hasPagePermission)
                        );

                        const hasPermission = item.isAccountMenu
                          ? hasOtherVisibleNavItems && isNavItemVisible(item, hasPagePermission)
                          : isNavItemVisible(item, hasPagePermission);

                        if (!hasPermission) {
                          return null;
                        }

                        return (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              alignItems: 'center',
                              mb: 0.25
                            }}
                            onMouseEnter={event => {
                              if (hasChildren) {
                                handleMenuOpen(event, index);
                              }
                            }}
                            onMouseLeave={() => {
                              if (hasChildren) {
                                handleMouseLeave(index);
                              }
                            }}
                          >
                            <ListItem
                              onClick={() => {
                                if (!hasChildren && item.link) {
                                  navigate(item.link);
                                  saveScroll();
                                }
                              }}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: 0,
                                cursor: 'pointer',
                                borderRadius: 0.5
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  my: 0.25,
                                  minWidth: 'unset',
                                  width: '18px',
                                  height: '18px',
                                  padding: '10px',
                                  borderRadius: '10px',
                                  border: '1px solid #ffffff',
                                  backgroundColor: isSelected ? '#ffffff' : '#FFFFFF66',
                                  color: theme.palette.primary.main,
                                  boxSizing: 'content-box',
                                  '& .SmIcon-root, & .MuiSvgIcon-root': {
                                    fontSize: 18,
                                    width: 18,
                                    height: 18,
                                    color: theme.palette.primary.main
                                  }
                                }}
                              >
                                {item.icon}
                              </ListItemIcon>
                              <Box
                                sx={{
                                  textAlign: 'center',
                                  whiteSpace: 'normal',
                                  maxWidth: drawerWidth
                                }}
                              >
                                <Typography variant="navLabel" color="primary.main">
                                  {item.primary}
                                </Typography>
                              </Box>
                            </ListItem>

                            <Divider sx={{ width: 70, borderColor: '#ffffff', borderBottomWidth: 2, mt: 1 }} />
                          </Box>
                        );
                      })}
                    </List>
                    <Box
                      onClick={showBottomIndicator ? scrollToBottom : undefined}
                      sx={{
                        flexShrink: 0,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: showBottomIndicator ? 'pointer' : 'default',
                        width: '100%',
                        opacity: showBottomIndicator ? 1 : 0,
                        pointerEvents: showBottomIndicator ? 'auto' : 'none',
                        transition: 'opacity 0.2s ease-in-out'
                      }}
                    >
                      <KeyboardDoubleArrowDownIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    </Box>
                  </Box>
                </Box>

                {/* ProfileSection - תמיד למטה */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    mt: 1,
                    flexShrink: 0, // מונע כיווץ
                    overflowX: 'hidden'
                  }}
                >
                  <ProfileSection
                    drawerWidth={drawerWidth}
                    logoSrc={isWorldShalichUser(userPermission) ? chabadLogoWorld : chabadLogo}
                    onLogoClick={() => navigate(isWorldShalichUser(userPermission) ? myPageRoute : '/')}
                  />
                </Box>
              </Box>
            )}

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                height: '100%',
                backgroundColor: isSmallScreen ? 'transparent' : 'white',
                borderRadius: isSmallScreen ? 0 : '20px',
                overflow: isSmallScreen ? 'hidden' : 'auto',
                minWidth: 0,
                boxSizing: 'border-box',
                // הסתרת סרגל גלילה גם בתוכן הראשי
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                  width: 0,
                  height: 0
                }
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  backgroundColor: isSmallScreen ? 'transparent' : bgColor || 'white',
                  borderRadius: isSmallScreen ? 0 : '20px',
                  pt: 1,
                  pb: 0,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* הוספת הכותרת הדינמית */}
                <Box sx={{ flexShrink: 0, pb: isSmallScreen ? 0 : 1, my: isSmallScreen ? 1 : 0, px: 2 }}>
                  <PageTitleHeader />
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    overflowY: isSmallScreen ? 'auto' : 'visible',
                    overflowX: isSmallScreen ? 'hidden' : 'visible',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                      width: 0,
                      height: 0
                    }
                  }}
                >
                  {handleInitialization()}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Mobile bottom nav + menu overlay (chrome only, sibling of the content tree) */}
          {isSmallScreen && <MobileDashboardChrome />}

          {/* Floating AI chat — renders only on /crm routes */}
          <CrmAiChat />

          {/* Welcome Dialog for World Shalich users */}
          {!isSmallScreen && isWorldShalichUser(userPermission) && (
            <NewDesignAnnouncementDialog
              open={showNewDesignDialog}
              onClose={handleNewDesignDialogClose}
              onDontShowAgain={handleDontShowAgain}
            />
          )}

          {/* <RequiredPersonalProfileDialog
            open={showRequiredProfileDialog}
            missingFields={missingFields}
            onContinue={goToProfile}
          /> */}


          <DynamicDialog
            open={showLogoutDialog}
            title="יציאה מהמערכת"
            subtitle="האם אתה בטוח שברצונך לצאת מהמערכת?"
            handleOkClose={() => {
              handleLogout();
              setShowLogoutDialog(false);
            }}
            handleNotOkClose={() => setShowLogoutDialog(false)}
          />
        </Box>
      </Box>
    </CrmAiChatProvider>
  );
};

export default DashboardLayout;
